// ledgerData — pure derivations shared by the v2 ledger screens.
//
// Everything here reads REAL store data (manifest entries + suite rows +
// cached topologies). Nothing fabricates a verdict: a scenario without a
// suite row has verdict `null` ("never ran"), which renders dim — not green.

import type {
  FlowEntry,
  ManifestEntry,
  SimulationScenario,
  SimulationTopology,
} from '../../types/index.ts';
import type { SuiteResult } from '../../store/slices/simulationSlice.ts';
import {
  classifyScenarioType,
  normalizeSuiteVerdict,
  type EffectiveVerdict,
  type ScenarioPathType,
} from '../simulation/scenarioBadges.ts';

/** Ordered kinds for ledger columns/groups. 'alt' only appears when present. */
export const LEDGER_KIND_ORDER: ScenarioPathType[] = [
  'happy',
  'failure',
  'negative',
  'timeout',
  'alt',
];

/** Verdicts from actual suite rows, keyed by scenarioId. */
export function verdictMapFromSuite(results: SuiteResult[]): Record<string, EffectiveVerdict> {
  const map: Record<string, EffectiveVerdict> = {};
  for (const r of results) {
    const v = normalizeSuiteVerdict(r.verdict);
    if (v !== null) map[r.scenarioId] = v;
  }
  return map;
}

export interface VerdictTally {
  total: number;
  pass: number;
  fail: number;
  error: number;
  skipped: number;
  none: number;
  /** Scenarios with no suite row at all — never ran. */
  notRun: number;
}

export function tallyVerdicts(
  entries: ManifestEntry[],
  verdicts: Record<string, EffectiveVerdict>,
): VerdictTally {
  const t: VerdictTally = {
    total: entries.length,
    pass: 0,
    fail: 0,
    error: 0,
    skipped: 0,
    none: 0,
    notRun: 0,
  };
  for (const e of entries) {
    const v = verdicts[e.scenarioId] ?? null;
    if (v === null) t.notRun += 1;
    else t[v] += 1;
  }
  return t;
}

/**
 * One-word flow/portfolio verdict, earned only from real rows:
 * - 'pass'    — every scenario ran and passed
 * - 'fail'    — at least one failed
 * - 'error'   — none failed but at least one errored
 * - 'partial' — no fails/errors but some skipped/inconclusive/not-run
 * - null      — nothing has run yet
 */
export type LedgerVerdict = 'pass' | 'fail' | 'error' | 'partial' | null;

export function ledgerVerdict(t: VerdictTally): LedgerVerdict {
  if (t.fail > 0) return 'fail';
  if (t.error > 0) return 'error';
  if (t.pass === t.total && t.total > 0) return 'pass';
  if (t.pass > 0 || t.skipped > 0 || t.none > 0) return 'partial';
  return null;
}

/** Display style for a non-null LedgerVerdict (portfolio rows + nav menus). */
export const LEDGER_VERDICT_LABELS = {
  pass: { label: '✓ PASS', color: 'var(--green)' },
  fail: { label: '✗ FAIL', color: 'var(--red)' },
  error: { label: '⚠ ERROR', color: '#fb923c' },
  partial: { label: '— PARTIAL', color: 'var(--text-muted)' },
} as const;

/**
 * Can this scenario be opened for the given source? The example source
 * fetches on demand; user/websocket manifests may declare scenarios whose
 * files were never uploaded/received — those must surface as disabled WITH a
 * reason, never as silently dead rows. Shared by the flow ledger and the
 * top-bar scenario menu so both tell the same truth.
 */
export function scenarioAvailability(
  source: 'example' | 'user' | 'websocket',
  flow: FlowEntry,
  entry: ManifestEntry,
  loadedTopologies: Record<string, SimulationTopology>,
  loadedScenarios: Record<string, SimulationScenario>,
): { ok: boolean; reason?: string } {
  if (source === 'example') return { ok: true };
  if (!Object.hasOwn(loadedTopologies, flow.flowId))
    return { ok: false, reason: `Topology for flow "${flow.flowName}" is not loaded` };
  if (!Object.hasOwn(loadedScenarios, entry.scenarioId))
    return { ok: false, reason: 'Scenario file is not loaded for this source' };
  return { ok: true };
}

export interface KindGroup {
  kind: ScenarioPathType;
  entries: ManifestEntry[];
  tally: VerdictTally;
}

/** Group a flow's scenarios by kind, in ledger order, dropping empty groups. */
export function groupByKind(
  flow: FlowEntry,
  verdicts: Record<string, EffectiveVerdict>,
): KindGroup[] {
  const groups: KindGroup[] = [];
  for (const kind of LEDGER_KIND_ORDER) {
    const entries = flow.scenarios.filter((e) => classifyScenarioType(e) === kind);
    if (entries.length > 0) groups.push({ kind, entries, tally: tallyVerdicts(entries, verdicts) });
  }
  return groups;
}

/** Human tally line for a kind group — never claims ALL PASS unless earned. */
export function tallyLine(t: VerdictTally): string {
  if (t.pass === t.total && t.total > 0) {
    return `${t.total} SCENARIO${t.total > 1 ? 'S' : ''} · ALL PASS`;
  }
  const parts: string[] = [];
  if (t.pass > 0) parts.push(`${t.pass} PASS`);
  if (t.fail > 0) parts.push(`${t.fail} FAIL`);
  if (t.error > 0) parts.push(`${t.error} ERROR`);
  if (t.skipped > 0) parts.push(`${t.skipped} SKIPPED`);
  if (t.none > 0) parts.push(`${t.none} INCONCLUSIVE`);
  if (t.notRun > 0) parts.push(`${t.notRun} NOT RUN`);
  return `${t.total} SCENARIO${t.total > 1 ? 'S' : ''} · ${parts.join(' · ') || 'NOT RUN'}`;
}

/** Topology-derived structural stats for a flow ledger's stat strip. */
export interface FlowStructure {
  moments: number;
  lanes: number;
  branchPoints: number;
  crossings: number;
  terminals: number;
}

export function flowStructureOf(topology: SimulationTopology | null): FlowStructure | null {
  if (!topology) return null;
  let terminals = 0;
  for (const frame of topology.frames) {
    for (const node of frame.nodes) if (node.terminal) terminals += 1;
  }
  return {
    moments: topology.frames.length,
    lanes: topology.lanes.length,
    branchPoints: topology.frames.filter((f) => f.isBranch).length,
    crossings: topology.connections.filter((c) => c.crossBoundary).length,
    terminals,
  };
}

/** Display label for the active source (top bar + ledger kickers). */
export function sourceDisplayLabel(
  source: 'example' | 'user' | 'websocket',
  wsSpecFile: string | null,
): string {
  if (source === 'user') return 'MY PROJECT · UPLOADED';
  if (source === 'websocket') {
    const spec = wsSpecFile ? (wsSpecFile.split('/').pop() ?? wsSpecFile) : 'moment serve';
    return `LIVE · ${spec}`;
  }
  return 'TICKETWAVE · BUNDLED EXAMPLE';
}

/** Split "Failure Path: foo [declined]" into base label + bracket variant tag. */
export function splitVariant(label: string): { base: string; variant: string | null } {
  const m = label.match(/^(.*?)\s*(\[[^\]]+\])\s*$/);
  if (!m) return { base: label, variant: null };
  return { base: m[1], variant: m[2] };
}
